import { useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { captableAction } from 'common/actions';
import {
  BREAKPOINT_ANALYSIS_SLUG,
  BREAKPOINT_ANALYSIS_TITLE,
  CAP_TABLE_SLUG,
  CAP_TABLE_TITLE,
  FUND_OWNERSHIP_SLUG,
  FUND_OWNERSHIP_TITLE,
} from 'common/constants/cap-table';
import { PDF_CONTENT_REF, PDF_FOOTER_REF, PDF_HEADER_REF } from 'common/constants/domRefs';
import { useCompanyBreadcrumb } from 'common/hooks';
import { useStore } from 'common/store';
import { PdfReportFooter, PdfReportHeader } from 'components';
import ExcelExportContext from 'context/ExcelExportContext';
import LayoutContext from 'context/LayoutContext';
import { CapTableDialog, NewCompanyDialog } from 'layouts/Main/components';
import useCapTableVersionFilter from 'pages/CapTable/cap-table/utilities/useCapTableVersionFilter';
import { useCompanyMeasurementDateFilter } from 'services/hooks';
import { useGetAllocationStatusBreadcrumb } from 'services/hooks/allocation';
import { getObjectValue } from 'utilities';
import BreakpointAnalysis from './breakpoint-analysis';
import CapTable from './cap-table';
import FundOwnership from './fund-ownership';

const CapTableView = () => {

  const { tableSlugParam } = useParams();
  const [{ captableInfo, companyInfo }, dispatch] = useStore();
  const { id: currentCapTableId } = getObjectValue(captableInfo);
  const { setCurrentSelectedValues } = useContext(ExcelExportContext);

  const { showPageForm, togglePageForm } = useContext(LayoutContext);
  const [showRelatedEntityDialog, setShowRelatedEntityDialog] = useState(false);
  const toggleRelatedEntityDialog = () => setShowRelatedEntityDialog(!showRelatedEntityDialog);
  // Filters options
  const { MDDialogue, selectedMeasurementDate } = useCompanyMeasurementDateFilter();
  const {
    selectedVersion,
    onEditCaptable,
    updateCapTableVersions,
    saveAsNewProforma,
    versionName,
    onAddRelatedEntity,
    capTableVersionsByCompany,
  } = useCapTableVersionFilter({ selectedMeasurementDate, onAddRelatedEntity: toggleRelatedEntityDialog });

  const { allocationStatusBreadcrumb, reloadAllocationBreadcrumbInfo } = useGetAllocationStatusBreadcrumb();

  const { isExportMode } = useContext(LayoutContext) as { isExportMode: boolean };

  useEffect(() => {
    setCurrentSelectedValues({
      cap_table: currentCapTableId,
      fo_cap_table: currentCapTableId,
      breakpoint_cap_table: currentCapTableId,
      waterfall_cap_table: currentCapTableId,
    });
  }, [currentCapTableId, setCurrentSelectedValues]);

  const activeTable = useMemo(() => {
    switch (tableSlugParam) {
      case CAP_TABLE_SLUG:
        return CAP_TABLE_TITLE;
      case FUND_OWNERSHIP_SLUG:
        return FUND_OWNERSHIP_TITLE;
      case BREAKPOINT_ANALYSIS_SLUG:
        return BREAKPOINT_ANALYSIS_TITLE;
      default:
        return '';
    }
  }, [tableSlugParam]);

  const { icon: status, title: label } = allocationStatusBreadcrumb;

  const captableBreadcrumb = useMemo(
    () => ({
      page: activeTable,
      item: versionName
        ? {
          label: versionName,
          allocationVersion: {
            label,
            status,
          },
        }
        : {},
    }),
    [activeTable, label, status, versionName]
  );

  useCompanyBreadcrumb(captableBreadcrumb);

  // Clean cap table info in the global store when leaving the page
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => dispatch(captableAction.setCaptableInfo({})), []);


  return (
    <>
      <PdfReportHeader title={companyInfo?.name} reportOnly={!isExportMode} data-ref={PDF_HEADER_REF} />
      
      {(selectedVersion || !selectedMeasurementDate) && tableSlugParam === CAP_TABLE_SLUG && (
        <div data-ref={PDF_CONTENT_REF}>
          <CapTable
            onEditCaptable={onEditCaptable}
            updateCapTableVersions={updateCapTableVersions}
            saveAsNewProforma={saveAsNewProforma}
            selectedMeasurementDate={selectedMeasurementDate}
            capTableId={currentCapTableId}
          />
        </div>
      )}
      {(selectedVersion || !selectedMeasurementDate) && tableSlugParam === FUND_OWNERSHIP_SLUG && (
        <div data-ref={PDF_CONTENT_REF}>
          <FundOwnership
            updateCapTableVersions={updateCapTableVersions}
            saveAsNewProforma={saveAsNewProforma}
            selectedMeasurementDate={selectedMeasurementDate}
            reloadAllocationBreadcrumbInfo={reloadAllocationBreadcrumbInfo}
            capTableVersionsByCompany={capTableVersionsByCompany}
          />
        </div>
      )}
      {tableSlugParam === BREAKPOINT_ANALYSIS_SLUG && (
        <div data-ref={PDF_CONTENT_REF}>
          <BreakpointAnalysis
            updateCapTableVersions={updateCapTableVersions}
            saveAsNewProforma={saveAsNewProforma}
            selectedMeasurementDate={selectedMeasurementDate}
          />
        </div>
      )}

      <PdfReportFooter reportOnly={!isExportMode} data-ref={PDF_FOOTER_REF} />

      <NewCompanyDialog
        open={showRelatedEntityDialog}
        onClose={toggleRelatedEntityDialog}
        onSave={onAddRelatedEntity}
        isRelatedEntity
      />

      {captableInfo && <CapTableDialog open={showPageForm} onClose={togglePageForm} onSave={onEditCaptable} />}
      <MDDialogue />

    </>
  );
};

export default CapTableView;

export { CapTableView, CapTable, FundOwnership, BreakpointAnalysis };
